"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTaskStream } from "@/hooks/useTaskStream";
import type { SnapshotTaskStatus } from "@/services/api/data-hub/types";
import type { Audit } from "@/services/api/guardiansv2/types";
import {
  computeFullAuditProgress,
  computeFullAuditProgressFromAudits,
  computeSingleAuditProgress,
  fullMissionBatchTaskIdsToRemove,
  loadPersistedLaunches,
  mergeLaunches,
  pruneFinishedBatchesFromAudits,
  savePersistedLaunches,
  type FullAuditProgress,
  type GuardiansAuditLaunch,
  type GuardiansAuditLaunchKind,
  type RegisterGuardiansAuditLaunchInput,
} from "./guardiansAuditLaunchStore";

const TERMINAL_STATUSES = new Set(["completed", "failed", "cancelled", "partial_failure"]);

export type { FullAuditProgress, GuardiansAuditLaunchKind };

interface GuardiansAuditLaunchContextValue {
  registerLaunches: (inputs: RegisterGuardiansAuditLaunchInput[]) => void;
  registerFullMissionAudit: (missionId: string, taskIds: string[], auditIds?: string[]) => void;
  isAuditInFlight: (auditId: string | null | undefined) => boolean;
  getFullAuditProgress: (
    scopeKey: string | null | undefined,
    audits?: Audit[]
  ) => FullAuditProgress;
  getSingleAuditProgress: (taskId: string | null | undefined) => FullAuditProgress;
  /** Drop tracked runner tasks after the UI has handled completion (e.g. alert issue redirect). */
  dismissLaunches: (taskIds: string[]) => void;
  reconcileWithAudits: (audits: Audit[]) => void;
  activeLaunchCount: number;
}

const INACTIVE_PROGRESS: FullAuditProgress = {
  active: false,
  finished: 0,
  total: 0,
  percent: 0,
};

const NOOP_GUARDIANS_AUDIT_LAUNCH: GuardiansAuditLaunchContextValue = {
  registerLaunches: () => {},
  registerFullMissionAudit: () => {},
  isAuditInFlight: () => false,
  getFullAuditProgress: () => INACTIVE_PROGRESS,
  getSingleAuditProgress: () => INACTIVE_PROGRESS,
  dismissLaunches: () => {},
  reconcileWithAudits: () => {},
  activeLaunchCount: 0,
};

const GuardiansAuditLaunchContext = createContext<GuardiansAuditLaunchContextValue | null>(null);

/** Tracks runner tasks for Guardians audit launches across views and aniOS window remounts. */
export function GuardiansAuditLaunchProvider({ children }: { children: ReactNode }) {
  const [launches, setLaunches] = useState<GuardiansAuditLaunch[]>(() => loadPersistedLaunches());
  const [statuses, setStatuses] = useState<Record<string, SnapshotTaskStatus>>({});
  const taskIds = useMemo(() => launches.map((launch) => launch.taskId), [launches]);
  const onAllCompleteRef = useRef<(() => void) | null>(null);

  const removeTaskIds = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    const drop = new Set(ids);
    setLaunches((prev) => {
      const next = prev.filter((launch) => !drop.has(launch.taskId));
      savePersistedLaunches(next);
      return next;
    });
  }, []);

  const { hasFailed, isCancelled } = useTaskStream(taskIds, {
    onAllComplete: () => onAllCompleteRef.current?.(),
    onStatusUpdate: (update) => {
      setStatuses((prev) => ({ ...prev, [update.task_id]: update }));
      if (!TERMINAL_STATUSES.has(update.status)) return;
      setLaunches((prev) => {
        const launch = prev.find((row) => row.taskId === update.task_id);
        if (launch?.kind === "full_mission" || launch?.kind === "single") return prev;
        const next = prev.filter((row) => row.taskId !== update.task_id);
        savePersistedLaunches(next);
        return next;
      });
    },
  });

  useEffect(() => {
    setStatuses((prev) => {
      const next: Record<string, SnapshotTaskStatus> = {};
      for (const id of taskIds) {
        if (prev[id]) next[id] = prev[id];
      }
      return next;
    });
  }, [taskIds]);

  useEffect(() => {
    onAllCompleteRef.current = () => {
      removeTaskIds(taskIds);
    };
  }, [removeTaskIds, taskIds]);

  useEffect(() => {
    if (taskIds.length === 0) return;
    if (!hasFailed && !isCancelled) return;
    removeTaskIds(taskIds);
  }, [hasFailed, isCancelled, removeTaskIds, taskIds]);

  useEffect(() => {
    if (launches.length === 0) return;
    const missionIds = new Set(
      launches
        .filter((launch) => launch.kind === "full_mission" && launch.missionId)
        .map((launch) => launch.missionId as string)
    );
    const toRemove = new Set<string>();
    for (const missionId of missionIds) {
      for (const taskId of fullMissionBatchTaskIdsToRemove(
        launches,
        missionId,
        statuses,
        TERMINAL_STATUSES
      )) {
        toRemove.add(taskId);
      }
    }
    if (toRemove.size > 0) removeTaskIds([...toRemove]);
  }, [launches, removeTaskIds, statuses]);

  const registerLaunches = useCallback((inputs: RegisterGuardiansAuditLaunchInput[]) => {
    if (inputs.length === 0) return;
    setLaunches((prev) => {
      const next = mergeLaunches(prev, inputs);
      savePersistedLaunches(next);
      return next;
    });
  }, []);

  const registerFullMissionAudit = useCallback(
    (missionId: string, taskIds: string[], auditIds?: string[]) => {
      const batchTotal = taskIds.length;
      registerLaunches(
        taskIds.map((taskId, index) => ({
          taskId,
          auditId: auditIds?.[index],
          missionId,
          kind: "full_mission" as const,
          batchTotal,
        }))
      );
    },
    [registerLaunches]
  );

  const reconcileWithAudits = useCallback((audits: Audit[]) => {
    if (audits.length === 0) return;
    setLaunches((prev) => {
      const next = pruneFinishedBatchesFromAudits(prev, audits);
      if (next.length === prev.length) return prev;
      savePersistedLaunches(next);
      return next;
    });
  }, []);

  const auditIdsInFlight = useMemo(() => {
    const ids = new Set<string>();
    for (const launch of launches) {
      if (launch.auditId) ids.add(launch.auditId);
    }
    return ids;
  }, [launches]);

  const isAuditInFlight = useCallback(
    (auditId: string | null | undefined) => {
      if (!auditId) return false;
      return auditIdsInFlight.has(auditId);
    },
    [auditIdsInFlight]
  );

  const getFullAuditProgress = useCallback(
    (scopeKey: string | null | undefined, audits?: Audit[]): FullAuditProgress => {
      if (!scopeKey) return INACTIVE_PROGRESS;
      if (audits && audits.length > 0) {
        return computeFullAuditProgressFromAudits(
          launches,
          scopeKey,
          audits,
          statuses,
          TERMINAL_STATUSES
        );
      }
      return computeFullAuditProgress(launches, scopeKey, statuses, TERMINAL_STATUSES);
    },
    [launches, statuses]
  );

  const getSingleAuditProgress = useCallback(
    (taskId: string | null | undefined): FullAuditProgress => {
      if (!taskId) return INACTIVE_PROGRESS;
      return computeSingleAuditProgress(launches, taskId, statuses, TERMINAL_STATUSES);
    },
    [launches, statuses]
  );

  const dismissLaunches = removeTaskIds;

  const value = useMemo(
    (): GuardiansAuditLaunchContextValue => ({
      registerLaunches,
      registerFullMissionAudit,
      isAuditInFlight,
      getFullAuditProgress,
      getSingleAuditProgress,
      dismissLaunches,
      reconcileWithAudits,
      activeLaunchCount: launches.length,
    }),
    [
      dismissLaunches,
      getFullAuditProgress,
      getSingleAuditProgress,
      isAuditInFlight,
      launches.length,
      reconcileWithAudits,
      registerFullMissionAudit,
      registerLaunches,
    ]
  );

  return (
    <GuardiansAuditLaunchContext.Provider value={value}>{children}</GuardiansAuditLaunchContext.Provider>
  );
}

export function useGuardiansAuditLaunch(): GuardiansAuditLaunchContextValue {
  return useContext(GuardiansAuditLaunchContext) ?? NOOP_GUARDIANS_AUDIT_LAUNCH;
}
